import React, { useMemo, useState } from 'react'
import { Input, Select, Tag } from 'antd'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { bulkPodApi, podBulkUploadApi } from '@/api/services'
import type {
  ApiResponse,
  BulkPodJob,
  BulkPodJobItemRow,
  BulkUploadBatchSummary,
  BulkUploadRollbackPreview,
  PodBulkUploadQueueItemRow,
} from '@/types'
import { Button } from '@/components/ui/Button'
import { DashboardPanel } from '@/components/dashboard'
import { leapTablePagination } from '@/components/table/defaultPagination'
import { LeapDataTable } from '@/components/table/LeapDataTable'
import BulkUploadKpiGrid from './BulkUploadKpiGrid'
import { BulkUploadElapsedTimer } from './BulkUploadProgress'
import {
  BULK_UPLOAD_OUTCOME_BREAKDOWN,
  buildBatchKpiItems,
  countForOutcomeBreakdown,
} from './bulkUploadOutcomeStats'
import RollbackConfirmModal from './RollbackConfirmModal'
import { useAuthStore } from '@/stores/authStore'

const TERMINAL_JOB = new Set(['completed', 'failed', 'cancelled'])

type BulkUploadResultsPanelProps = {
  mode: 'local' | 'cloud'
  batchId?: string | null
  jobId?: number | null
  enabled: boolean
  isRunning?: boolean
  rolledBack?: boolean
  runTimer?: { startedAt: number; endedAt: number | null } | null
  onResumeProcessing?: () => void
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const BulkUploadResultsPanel: React.FC<BulkUploadResultsPanelProps> = ({
  mode,
  batchId,
  jobId,
  enabled,
  isRunning = false,
  rolledBack = false,
  runTimer = null,
  onResumeProcessing,
}) => {
  const { t } = useTranslation(['BulkPodUpload', 'Common'])
  const qc = useQueryClient()
  const [itemPage, setItemPage] = useState({ page: 1, pageSize: 25 })
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [outcomeFilter, setOutcomeFilter] = useState<string | undefined>()
  const [search, setSearch] = useState('')
  const [rollbackOpen, setRollbackOpen] = useState(false)
  const [rollbackPreview, setRollbackPreview] = useState<BulkUploadRollbackPreview | null>(null)
  const [rollbackLoading, setRollbackLoading] = useState(false)

  const active = enabled && (mode === 'local' ? !!batchId : !!jobId)

  const localStatusQuery = useQuery({
    queryKey: ['bulk-upload-batch-status', batchId],
    queryFn: async () => {
      const res: any = await podBulkUploadApi.queueStatus({ batchId: batchId! })
      return res?.data as {
        batchSummary?: BulkUploadBatchSummary | null
        queue?: Record<string, number>
      }
    },
    enabled: active && mode === 'local' && !!batchId,
    refetchInterval: false,
  })

  const jobQuery = useQuery({
    queryKey: ['bulk-pod-job', jobId],
    queryFn: async () => {
      const res = (await bulkPodApi.getJob(jobId!)) as any
      return res?.data as ApiResponse<BulkPodJob>
    },
    enabled: active && mode === 'cloud' && !!jobId,
    refetchInterval: isRunning ? 3000 : false,
  })

  const localItemsQuery = useQuery({
    queryKey: [
      'bulk-upload-queue-items',
      batchId,
      itemPage.page,
      itemPage.pageSize,
      statusFilter,
      outcomeFilter,
      search,
    ],
    queryFn: async () => {
      const offset = (itemPage.page - 1) * itemPage.pageSize
      const res: any = await podBulkUploadApi.getQueueItems({
        batchId: batchId!,
        limit: itemPage.pageSize,
        offset,
        status: statusFilter,
        outcome: outcomeFilter,
        search: search || undefined,
      })
      return res?.data as ApiResponse<PodBulkUploadQueueItemRow[]> & { total?: number }
    },
    enabled: active && mode === 'local' && !!batchId,
    refetchInterval: isRunning ? 8000 : false,
  })

  const cloudItemsQuery = useQuery({
    queryKey: ['bulk-pod-job-items', jobId, itemPage.page, itemPage.pageSize, statusFilter],
    queryFn: async () => {
      const offset = (itemPage.page - 1) * itemPage.pageSize
      const res: any = await bulkPodApi.getJobItems(jobId!, {
        limit: itemPage.pageSize,
        offset,
        status: statusFilter,
      })
      return res?.data as ApiResponse<BulkPodJobItemRow[]> & { total?: number }
    },
    enabled: active && mode === 'cloud' && !!jobId,
    refetchInterval: isRunning ? 4000 : false,
  })

  const batchSummary = localStatusQuery.data?.batchSummary ?? null
  const job = jobQuery.data?.data
  const localRows = (localItemsQuery.data?.data ?? []) as PodBulkUploadQueueItemRow[]
  const cloudRows = (cloudItemsQuery.data?.data ?? []) as BulkPodJobItemRow[]
  const itemTotal = Number(
    mode === 'local' ? localItemsQuery.data?.total ?? 0 : cloudItemsQuery.data?.total ?? 0
  )
  const batchFileTotal = batchSummary?.totalFiles ?? itemTotal
  const rateLimitedCount =
    batchSummary?.failed_rate_limit ?? batchSummary?.outcomes?.failed_rate_limit ?? 0

  const kpiItems = useMemo(
    () => (batchSummary ? buildBatchKpiItems(batchSummary) : []),
    [batchSummary]
  )

  const visibleOutcomeBreakdown = useMemo(
    () =>
      BULK_UPLOAD_OUTCOME_BREAKDOWN.filter(row => {
        if (!row.outcomeKey) return true
        return countForOutcomeBreakdown(batchSummary, row.outcomeKey) > 0 || isRunning
      }),
    [batchSummary, isRunning]
  )

  const activeFilterLabel = useMemo(() => {
    if (!outcomeFilter && !statusFilter) return null
    const outcomeRow = BULK_UPLOAD_OUTCOME_BREAKDOWN.find(r => r.outcomeKey === (outcomeFilter ?? ''))
    if (outcomeFilter && outcomeRow) {
      return t(`BulkPodUpload:${outcomeRow.labelKey}`, outcomeRow.fallback)
    }
    if (statusFilter) return statusFilter
    return null
  }, [outcomeFilter, statusFilter, t])

  const formatStatusTag = (st: string) => {
    const upper = String(st).toUpperCase()
    const color =
      upper === 'COMPLETED' || st === 'success'
        ? 'success'
        : upper === 'FAILED' || st === 'error'
          ? 'error'
          : upper === 'SKIPPED' || st === 'skipped'
            ? 'warning'
            : upper === 'ROLLED_BACK' || st === 'rolled_back'
              ? 'default'
              : upper === 'PROCESSING' || st === 'processing'
                ? 'processing'
                : 'default'
    return <Tag color={color as any}>{st}</Tag>
  }

  const localColumns = useMemo(
    () => [
      { title: t('BulkPodUpload:columns.file', 'File'), dataIndex: 'fileName', key: 'fileName', ellipsis: true, width: 200 },
      { title: t('BulkPodUpload:columns.docNo', 'Doc №'), dataIndex: 'documentNo', key: 'documentNo', width: 120 },
      {
        title: t('BulkPodUpload:columns.columnStatus', 'Status'),
        dataIndex: 'processingStatus',
        key: 'processingStatus',
        width: 120,
        render: (st: string) => formatStatusTag(st),
      },
      {
        title: t('BulkPodUpload:columns.outcome', 'Outcome'),
        dataIndex: 'outcome',
        key: 'outcome',
        width: 200,
        render: (v: string | null) => (v ? t(`BulkPodUpload:outcomes.${v}`, v) : '—'),
      },
      {
        title: t('BulkPodUpload:columns.approvalId', 'Approval ID'),
        dataIndex: 'approvalId',
        key: 'approvalId',
        width: 110,
        render: (v: number | null) => v ?? '—',
      },
      {
        title: t('BulkPodUpload:columns.error', 'Error'),
        dataIndex: 'errorMessage',
        key: 'errorMessage',
        ellipsis: true,
      },
      {
        title: t('BulkPodUpload:columns.updatedAt', 'Updated'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 160,
        render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—'),
      },
    ],
    [t]
  )

  const cloudColumns = useMemo(
    () => [
      { title: t('BulkPodUpload:columns.file', 'File'), dataIndex: 'fileName', key: 'fileName', ellipsis: true, width: 200 },
      { title: t('BulkPodUpload:columns.docNo', 'Doc №'), dataIndex: 'parsedDocumentNo', key: 'parsedDocumentNo', width: 120 },
      { title: t('BulkPodUpload:columns.source', 'Source'), dataIndex: 'sourceProvider', key: 'sourceProvider', width: 118 },
      {
        title: t('BulkPodUpload:columns.columnStatus', 'Status'),
        dataIndex: 'rowStatus',
        key: 'rowStatus',
        width: 118,
        render: (st: string) => formatStatusTag(st),
      },
      {
        title: t('BulkPodUpload:columns.approvalId', 'Approval ID'),
        dataIndex: 'approvalId',
        key: 'approvalId',
        width: 110,
        render: (v: number | null) => v ?? '—',
      },
      {
        title: t('BulkPodUpload:columns.error', 'Error'),
        dataIndex: 'errorMessage',
        key: 'errorMessage',
        ellipsis: true,
      },
    ],
    [t]
  )

  const handleExport = async () => {
    try {
      if (mode === 'local' && batchId) {
        const blob = await podBulkUploadApi.exportQueueItems({ batchId })
        downloadBlob(blob, `Bulk_Upload_Results_${dayjs().format('YYYY-MM-DD')}.xlsx`)
      } else if (mode === 'cloud' && jobId) {
        const blob = await bulkPodApi.exportJobItems(jobId)
        downloadBlob(blob, `Cloud_Bulk_Upload_Job_${jobId}_${dayjs().format('YYYY-MM-DD')}.xlsx`)
      }
      toast.success(t('BulkPodUpload:export.success', 'Export downloaded'))
    } catch (err: any) {
      toast.error(err?.message || t('BulkPodUpload:export.failed', 'Export failed'))
    }
  }

  const handleRollbackPreview = async () => {
    try {
      setRollbackLoading(true)
      if (mode === 'local' && batchId) {
        const res: any = await podBulkUploadApi.rollbackPreview(batchId)
        setRollbackPreview(res?.data?.data ?? res?.data)
      } else if (mode === 'cloud' && jobId) {
        const res: any = await bulkPodApi.rollbackPreview(jobId)
        setRollbackPreview(res?.data?.data ?? res?.data)
      }
      setRollbackOpen(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Rollback preview failed')
    } finally {
      setRollbackLoading(false)
    }
  }

  const handleRollbackConfirm = async () => {
    try {
      setRollbackLoading(true)
      if (mode === 'local' && batchId) {
        await podBulkUploadApi.rollbackBatch(batchId)
        qc.invalidateQueries({ queryKey: ['bulk-upload-queue-items', batchId] })
        qc.invalidateQueries({ queryKey: ['bulk-upload-batch-status', batchId] })
      } else if (mode === 'cloud' && jobId) {
        await bulkPodApi.rollbackJob(jobId)
        qc.invalidateQueries({ queryKey: ['bulk-pod-job', jobId] })
        qc.invalidateQueries({ queryKey: ['bulk-pod-job-items', jobId] })
      }
      toast.success(t('BulkPodUpload:rollback.success', 'Upload reverted successfully'))
      setRollbackOpen(false)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Rollback failed')
    } finally {
      setRollbackLoading(false)
    }
  }

  const clearFilters = () => {
    setStatusFilter(undefined)
    setOutcomeFilter(undefined)
    setSearch('')
    setItemPage(p => ({ ...p, page: 1 }))
  }

  const handleRetryRateLimited = async () => {
    if (mode !== 'local' || !batchId) return
    try {
      const res: any = await podBulkUploadApi.processQueue({ batchId, retryRateLimited: true })
      const requeued = Number(res?.data?.requeuedRateLimited ?? 0)
      toast.success(
        t('BulkPodUpload:messages.retryRateLimited', 'Re-queued {{count}} rate-limited files for processing', {
          count: requeued || rateLimitedCount,
        })
      )
      qc.invalidateQueries({ queryKey: ['bulk-upload-batch-status', batchId] })
      qc.invalidateQueries({ queryKey: ['bulk-upload-queue-items', batchId] })
      onResumeProcessing?.()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Retry failed')
    }
  }

  const { user } = useAuthStore()
  const canPerformBulkUpload = user?.permissions?.includes('pod.bulk_upload') ?? false

  const canRollback =
    canPerformBulkUpload &&
    !rolledBack &&
    !isRunning &&
    (mode === 'local'
      ? !!batchId
      : !!jobId && job?.status && TERMINAL_JOB.has(job.status) && !job.rolledBackAt)

  if (!active) return null

  return (
    <>
      <DashboardPanel
        title={t('BulkPodUpload:summary.summaryTitle', 'Batch results')}
        subtitle={
          mode === 'local' && batchSummary
            ? t('BulkPodUpload:stats.tableScope', '{{total}} files in this batch', {
                total: batchSummary.totalFiles,
              })
            : mode === 'local' && isRunning
              ? t('BulkPodUpload:stats.loadingBatch', 'Loading batch statistics…')
              : t('BulkPodUpload:summary.summarySubtitle', 'Counts below match the file table — click a category to filter rows')
        }
        actions={
          <>
            {runTimer?.endedAt ? (
              <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2.5 py-1 text-xs font-medium text-foreground">
                <span className="text-muted-foreground">{t('BulkPodUpload:summary.elapsed', 'Total time')}</span>
                <BulkUploadElapsedTimer startedAt={runTimer.startedAt} endedAt={runTimer.endedAt} />
              </span>
            ) : null}
            <Button type="button" variant="outline" size="sm" onClick={handleExport}>
              {t('BulkPodUpload:export.button', 'Download Excel')}
            </Button>
            {mode === 'local' && !isRunning && rateLimitedCount > 0 ? (
              <Button type="button" variant="outline" size="sm" onClick={handleRetryRateLimited}>
                {t('BulkPodUpload:retry.rateLimited', 'Retry rate-limited ({{count}})', {
                  count: rateLimitedCount,
                })}
              </Button>
            ) : null}
            {canRollback ? (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleRollbackPreview}
                disabled={rollbackLoading}
              >
                {t('BulkPodUpload:rollback.button', 'Revert upload')}
              </Button>
            ) : null}
            {rolledBack || job?.rolledBackAt ? (
              <Tag color="default">{t('BulkPodUpload:rollback.badge', 'Rolled back')}</Tag>
            ) : null}
          </>
        }
      >
        <div className="space-y-5 p-4">
          {mode === 'local' && (batchSummary || isRunning) ? (
            <>
              {batchSummary ? (
                <>
                  <BulkUploadKpiGrid items={kpiItems} />
                  <p className="text-sm text-muted-foreground">
                    {t(
                      'BulkPodUpload:stats.filterHint',
                      'Click a category to filter the file table below'
                    )}
                  </p>
                  <div className="flex flex-wrap gap-2 rounded-lg border border-border bg-muted/20 p-3">
                    {visibleOutcomeBreakdown.map(row => {
                      const count = countForOutcomeBreakdown(batchSummary, row.outcomeKey)
                      const selected = (outcomeFilter || '') === row.outcomeKey && !statusFilter
                      return (
                        <button
                          key={row.outcomeKey || 'all'}
                          type="button"
                          onClick={() => {
                            setOutcomeFilter(row.outcomeKey || undefined)
                            setStatusFilter(undefined)
                            setItemPage(p => ({ ...p, page: 1 }))
                          }}
                          className={`rounded-md border px-3 py-1.5 text-left text-xs transition-colors ${
                            selected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-background text-foreground hover:bg-accent'
                          }`}
                        >
                          <span className="font-medium">
                            {t(`BulkPodUpload:${row.labelKey}`, row.fallback)}
                          </span>
                          <span className={`ml-2 tabular-nums ${selected ? '' : 'text-muted-foreground'}`}>
                            {count}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('BulkPodUpload:stats.loadingBatch', 'Loading batch statistics…')}
                </p>
              )}
            </>
          ) : null}

          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
            <Select
              allowClear
              placeholder={t('BulkPodUpload:filters.status', 'Filter by status')}
              className="min-w-[160px]"
              value={statusFilter}
              onChange={v => {
                setStatusFilter(v)
                setItemPage(p => ({ ...p, page: 1 }))
              }}
              options={
                mode === 'local'
                  ? ['PENDING', 'PROCESSING', 'COMPLETED', 'SKIPPED', 'FAILED', 'ROLLED_BACK'].map(s => ({
                      label: s,
                      value: s,
                    }))
                  : ['pending', 'processing', 'success', 'skipped', 'error', 'rolled_back'].map(s => ({
                      label: s,
                      value: s,
                    }))
              }
            />
            {mode === 'local' ? (
              <Select
                allowClear
                placeholder={t('BulkPodUpload:filters.outcome', 'Filter by outcome')}
                className="min-w-[220px]"
                value={outcomeFilter}
                onChange={v => {
                  setOutcomeFilter(v)
                  setItemPage(p => ({ ...p, page: 1 }))
                }}
                options={BULK_UPLOAD_OUTCOME_BREAKDOWN.filter(r => r.outcomeKey).map(row => ({
                  label: t(`BulkPodUpload:${row.labelKey}`, row.fallback),
                  value: row.outcomeKey,
                }))}
              />
            ) : null}
            <Input.Search
              allowClear
              placeholder={t('BulkPodUpload:filters.search', 'Search doc № or filename')}
              className="max-w-xs"
              onSearch={v => {
                setSearch(v)
                setItemPage(p => ({ ...p, page: 1 }))
              }}
            />
            {statusFilter || outcomeFilter || search ? (
              <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                {t('Common:general.reset', 'Reset')}
              </Button>
            ) : null}
          </div>

          {activeFilterLabel ? (
            <p className="text-sm text-muted-foreground">
              {t('BulkPodUpload:stats.showingFiltered', 'Showing {{shown}} of {{total}} files in batch', {
                shown: itemTotal,
                total: batchFileTotal,
              })}
              {' — '}
              <span className="font-medium text-foreground">{activeFilterLabel}</span>
            </p>
          ) : null}

          {mode === 'local' ? (
            <LeapDataTable<PodBulkUploadQueueItemRow>
              dataSource={localRows}
              columns={localColumns}
              rowKey="id"
              loading={localItemsQuery.isLoading}
              pagination={{
                ...leapTablePagination(itemTotal),
                current: itemPage.page,
                pageSize: itemPage.pageSize,
                total: itemTotal,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  outcomeFilter || statusFilter || search
                    ? t('BulkPodUpload:stats.showingFiltered', 'Showing {{shown}} of {{total}} files in batch', {
                        shown: total,
                        total: batchFileTotal,
                      }) + ` (${range[0]}-${range[1]})`
                    : `${range[0]}-${range[1]} of ${total}`,
                onChange: (page, pageSize) =>
                  setItemPage({ page, pageSize: pageSize ?? itemPage.pageSize }),
              }}
            />
          ) : (
            <LeapDataTable<BulkPodJobItemRow>
              dataSource={cloudRows}
              columns={cloudColumns}
              rowKey="id"
              loading={cloudItemsQuery.isLoading}
              pagination={{
                ...leapTablePagination(itemTotal),
                current: itemPage.page,
                pageSize: itemPage.pageSize,
                total: itemTotal,
                showSizeChanger: true,
                onChange: (page, pageSize) =>
                  setItemPage({ page, pageSize: pageSize ?? itemPage.pageSize }),
              }}
            />
          )}
        </div>
      </DashboardPanel>

      <RollbackConfirmModal
        open={rollbackOpen}
        preview={rollbackPreview}
        loading={rollbackLoading}
        onConfirm={handleRollbackConfirm}
        onCancel={() => setRollbackOpen(false)}
      />
    </>
  )
}

export default BulkUploadResultsPanel
